import type { Explained, ExerciseLoadParams, Term } from './types';

/**
 * Expected RPE from how an exercise is designed.
 *
 * This replaces asking thirty youth players to rate a session honestly every
 * day. It is a model, labelled as one everywhere it appears — it estimates
 * *load*, and cannot know that a player slept four hours.
 *
 * The factors are the ones that actually move perceived exertion in football,
 * and each is applied as a named, visible adjustment rather than folded into an
 * opaque score:
 *
 *  - the part of the session it belongs to sets the base
 *  - area per player: the strongest lever in any game-based exercise
 *  - work-to-rest: incomplete recovery raises perceived effort sharply
 *  - energy-system emphasis: lactic work feels hardest, alactic work is maximal
 *    but well rested and produces a lower session RPE than coaches expect
 *  - total work volume
 *
 * A coach who states an intensity overrides the estimate entirely — their
 * judgement of their own exercise beats a model of it, and the model's job then
 * is only to explain what they chose.
 */

const SCALE_MIN = 1;
const SCALE_MAX = 10;

/** Starting point per part of the session, before any adjustment. */
const CATEGORY_BASE: Record<string, number> = {
  warmup: 3,
  activation: 3,
  cooldown: 2,
  technical: 4,
  set_piece: 4,
  goalkeeping: 5,
  tactical: 5,
  possession: 6,
  small_sided: 7,
  game: 7,
  conditioning: 8,
};

const DEFAULT_BASE = 5;

/** Rounds to the nearest half point: the resolution an RPE scale actually has. */
function toHalf(n: number): number {
  return Math.round(n * 2) / 2;
}

function clamp(n: number): number {
  return Math.min(SCALE_MAX, Math.max(SCALE_MIN, n));
}

export function areaPerPlayer(p: ExerciseLoadParams): number | null {
  const players = p.maxPlayers ?? p.minPlayers;
  if (!p.areaLength || !p.areaWidth || !players || players <= 0) return null;
  return (p.areaLength * p.areaWidth) / players;
}

/**
 * Density adjustment. Below roughly 100 m² per player a game becomes a
 * continuous duel; above 250 it turns into running with space, where perceived
 * effort falls even as distance rises.
 */
function densityTerm(perPlayer: number | null): Term | null {
  if (perPlayer === null) return null;
  let delta: number;
  if (perPlayer < 50) delta = 1;
  else if (perPlayer < 100) delta = 0.5;
  else if (perPlayer < 150) delta = 0;
  else if (perPlayer < 250) delta = -0.3;
  else delta = -0.6;
  return {
    key: 'load.term.density',
    delta,
    detail: { areaPerPlayer: Math.round(perPlayer * 10) / 10 },
  };
}

/**
 * Work-to-rest adjustment. Rest at three times the work duration preserves
 * quality and keeps perceived effort low; rest under half the work duration
 * accumulates fatigue within the exercise itself.
 */
function workRestTerm(p: ExerciseLoadParams): Term | null {
  const work = p.workSeconds;
  if (!work || work <= 0) return null;
  const rest = p.restSeconds;
  if (rest === null || rest === undefined) return null;

  if (rest === 0) {
    return { key: 'load.term.continuous', delta: 0.5, detail: { workSeconds: work } };
  }

  const ratio = rest / work;
  let delta: number;
  if (ratio >= 3) delta = -0.8;
  else if (ratio >= 1.5) delta = -0.3;
  else if (ratio >= 0.5) delta = 0;
  else delta = 0.8;

  return {
    key: 'load.term.workRest',
    delta,
    detail: { ratio: Math.round(ratio * 100) / 100, workSeconds: work, restSeconds: rest },
  };
}

/**
 * Energy-system adjustment.
 *
 * Alactic work lowers the estimate rather than raising it, which surprises
 * people: the efforts are maximal but short and fully recovered, so a session of
 * sprints is rated lower than a possession game — and rating it higher is how
 * coaches end up accumulating fatigue they cannot account for.
 */
function energyTerm(system: string | null | undefined): Term | null {
  if (!system) return null;
  const deltas: Record<string, number> = {
    anaerobic_lactic: 0.8,
    aerobic: 0,
    anaerobic_alactic: -0.5,
  };
  const delta = deltas[system];
  if (delta === undefined) return null;
  return { key: 'load.term.energy', delta, detail: { system } };
}

/** Total work across sets and reps, in minutes. */
export function totalWorkMinutes(p: ExerciseLoadParams): number | null {
  if (!p.workSeconds) return null;
  return (p.workSeconds * (p.reps ?? 1) * (p.sets ?? 1)) / 60;
}

function volumeTerm(p: ExerciseLoadParams): Term | null {
  const total = totalWorkMinutes(p) ?? p.durationMin ?? null;
  if (total === null) return null;
  let delta: number;
  if (total >= 30) delta = 0.5;
  else if (total >= 12) delta = 0;
  else if (total >= 8) delta = -0.25;
  else delta = -0.5;
  return {
    key: 'load.term.volume',
    delta,
    detail: { totalWorkMinutes: Math.round(total * 10) / 10 },
  };
}

export function estimateExerciseRpe(p: ExerciseLoadParams): Explained {
  const inputs = {
    categoryKey: p.categoryKey ?? null,
    durationMin: p.durationMin ?? null,
    intensity: p.intensity ?? null,
    players: p.maxPlayers ?? p.minPlayers ?? null,
    areaLength: p.areaLength ?? null,
    areaWidth: p.areaWidth ?? null,
    workSeconds: p.workSeconds ?? null,
    restSeconds: p.restSeconds ?? null,
    primaryEnergySystem: p.primaryEnergySystem ?? null,
  };

  // The coach's own number wins. The model then only has to explain it.
  if (p.intensity !== null && p.intensity !== undefined) {
    return {
      value: clamp(toHalf(p.intensity)),
      formula: 'load.formula.coachStated',
      inputs,
      terms: [{ key: 'load.term.coachStated', delta: p.intensity }],
    };
  }

  const base = p.categoryKey ? (CATEGORY_BASE[p.categoryKey] ?? DEFAULT_BASE) : DEFAULT_BASE;
  const terms: Term[] = [
    { key: 'load.term.base', delta: base, detail: { categoryKey: p.categoryKey ?? null } },
  ];

  for (const term of [
    densityTerm(areaPerPlayer(p)),
    workRestTerm(p),
    energyTerm(p.primaryEnergySystem),
    volumeTerm(p),
  ]) {
    if (term) terms.push(term);
  }

  const raw = terms.reduce((sum, t) => sum + t.delta, 0);
  return {
    value: clamp(toHalf(raw)),
    formula: 'load.formula.estimated',
    inputs,
    terms,
  };
}

export interface SessionExercise {
  id?: string;
  durationMin: number;
  rpe: number;
}

/**
 * Session RPE, weighted by how long each exercise lasts.
 *
 * A plain average would let a five-minute activation drag down an hour of
 * small-sided games. Time in each exercise is what the players actually did.
 */
export function estimateSessionRpe(exercises: readonly SessionExercise[]): Explained {
  const usable = exercises.filter((e) => e.durationMin > 0);
  const totalMinutes = usable.reduce((sum, e) => sum + e.durationMin, 0);

  if (totalMinutes === 0) {
    return {
      value: 0,
      formula: 'load.formula.sessionRpe',
      inputs: { totalMinutes: 0, exercises: exercises.length },
      terms: [],
    };
  }

  const weighted = usable.reduce((sum, e) => sum + e.rpe * e.durationMin, 0);
  return {
    value: Math.round((weighted / totalMinutes) * 10) / 10,
    formula: 'load.formula.sessionRpe',
    inputs: { totalMinutes, exercises: usable.length },
    terms: usable.map((e) => ({
      key: 'load.term.exerciseShare',
      delta: Math.round(((e.rpe * e.durationMin) / totalMinutes) * 100) / 100,
      detail: { durationMin: e.durationMin, rpe: e.rpe },
    })),
  };
}

/**
 * Session load in arbitrary units: Foster's session-RPE, duration × RPE.
 *
 * Deliberately kept separate from the stimulus profile. Two sessions can share a
 * load of 480 AU and do entirely different things to a player — which is the
 * distinction the brief insists the product must not blur.
 */
export function sessionLoad(durationMin: number, rpe: number): Explained {
  const value = Math.round(durationMin * rpe);
  return {
    value,
    formula: 'load.formula.sessionLoad',
    inputs: { durationMin, rpe },
    terms: [
      { key: 'load.term.duration', delta: durationMin },
      { key: 'load.term.rpe', delta: rpe },
    ],
  };
}
